import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "@/components/ui/sonner";
import { Edit2, Archive, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  personal:  { color: "#92400e", bg: "rgba(212,165,116,0.15)", label: "Personal"  },
  financial: { color: "#3d6b1f", bg: "rgba(61,107,31,0.12)",   label: "Financial" },
  career:    { color: "#1A4D2E", bg: "rgba(26,77,46,0.12)",    label: "Career"    },
  health:    { color: "#dc2626", bg: "rgba(220,38,38,0.1)",    label: "Health"    },
  other:     { color: "#6b7280", bg: "rgba(107,114,128,0.1)",  label: "Other"     },
};

const PRIORITY_STYLES = {
  high:   { color: "#dc2626", bg: "rgba(220,38,38,0.08)",   label: "High"   },
  medium: { color: "#d97706", bg: "rgba(217,119,6,0.08)",   label: "Medium" },
  low:    { color: "#3b82f6", bg: "rgba(59,130,246,0.08)",  label: "Low"    },
};

function timeRemaining(targetDate) {
  if (!targetDate) return null;
  const diffDays = Math.round((new Date(targetDate) - new Date()) / 86400000);
  if (diffDays < 0)  return { text: `Overdue by ${Math.abs(diffDays)} days`, overdue: true };
  if (diffDays === 0) return { text: "Due today", overdue: false };
  if (diffDays < 30)  return { text: `${diffDays}d left`, overdue: false };
  const months = Math.round(diffDays / 30);
  return { text: `${months}mo left`, overdue: false };
}

function formatAmount(amount, unit) {
  if (!amount) return null;
  const num = amount >= 1000000 ? `${(amount / 1000000).toFixed(1)}M`
            : amount >= 1000    ? `${(amount / 1000).toFixed(0)}k`
            : amount.toString();
  return unit ? `${unit}${num}` : num;
}

export default function GoalCard({ goal, onGamificationData, onEdit, onArchive, onDelete, onGoalUpdated }) {
  const { token } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const [newItemText, setNewItemText] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const [optimisticItems, setOptimisticItems] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const items = optimisticItems ?? goal.items ?? [];
  const visibleItems = showAll ? items : items.slice(0, 10);
  const hiddenCount = items.length - 10;
  const cat = CATEGORY_STYLES[goal.category] || CATEGORY_STYLES.other;
  const pri = PRIORITY_STYLES[goal.priority] || PRIORITY_STYLES.medium;
  const timeLeft = timeRemaining(goal.target_date);
  const amountStr = formatAmount(goal.target_amount, goal.target_unit);
  const doneCount = items.filter((i) => i.is_completed).length;

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleToggle = async (item) => {
    // Optimistic update
    const updated = items.map((i) =>
      i.id === item.id ? { ...i, is_completed: !i.is_completed } : i
    );
    setOptimisticItems(updated);
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/goal-items/${item.id}/toggle`, {}, { headers: authHeaders }
      );
      setOptimisticItems(null);
      onGoalUpdated(data.goal);
      if (onGamificationData && data.xp_earned > 0) onGamificationData(data);
    } catch {
      setOptimisticItems(null);
      toast.error("Failed to update item");
    }
  };

  const handleAddItem = async () => {
    const text = newItemText.trim();
    if (!text) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/goals/${goal.id}/items`,
        { title: text },
        { headers: authHeaders }
      );
      setNewItemText("");
      setAddingItem(false);
      onGoalUpdated(data);
    } catch {
      toast.error("Failed to add item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/goal-items/${itemId}`, { headers: authHeaders });
      onGoalUpdated(data);
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const progressGradient = goal.progress_percentage === 100
    ? "linear-gradient(90deg, #1A4D2E, #86c559)"
    : `linear-gradient(90deg, #1A4D2E ${goal.progress_percentage}%, #3d6b1f)`;

  return (
    <div
      data-testid={`goal-card-${goal.id}`}
      style={{
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.7)",
        borderRadius: "0.875rem",
        boxShadow: "0 4px 24px rgba(26,77,46,0.06)",
        padding: "1.25rem 1.4rem",
        marginBottom: "0.875rem",
        transition: "box-shadow 0.2s",
      }}
    >
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.6rem" }}>
        <h3 style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "1.05rem", fontWeight: 700, margin: 0, lineHeight: 1.3, flex: 1 }}>
          {goal.status === "completed" && <span style={{ marginRight: "0.35rem", color: "#3d6b1f" }}>✓</span>}
          {goal.title}
        </h3>
        <span style={{
          flexShrink: 0, borderRadius: "2rem", padding: "0.2rem 0.65rem",
          fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", fontWeight: 700,
          color: cat.color, background: cat.bg, textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          {cat.label}
        </span>
      </div>

      {/* ── Metadata row ── */}
      <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap", alignItems: "center", marginBottom: "0.875rem" }}>
        {timeLeft && (
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", color: timeLeft.overdue ? "#dc2626" : "#6b7280" }}>
            {timeLeft.overdue ? "⚠ " : ""}Target: {timeLeft.text}
          </span>
        )}
        {amountStr && (
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", color: "#374151", fontWeight: 600 }}>
            {amountStr}
          </span>
        )}
        <span style={{
          borderRadius: "2rem", padding: "0.15rem 0.55rem",
          fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem", fontWeight: 700,
          color: pri.color, background: pri.bg,
        }}>
          {pri.label}
        </span>
      </div>

      {/* ── Progress bar ── */}
      {items.length > 0 && (
        <div style={{ marginBottom: "0.875rem" }}>
          <div style={{ height: "8px", background: "rgba(26,77,46,0.1)", borderRadius: "4px", overflow: "hidden", marginBottom: "0.3rem" }}>
            <div style={{
              height: "100%", width: `${goal.progress_percentage}%`,
              background: progressGradient, borderRadius: "4px",
              transition: "width 0.5s ease",
            }} />
          </div>
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.75rem" }}>
            {doneCount}/{items.length} items complete ({goal.progress_percentage}%)
          </span>
        </div>
      )}

      {/* ── Checklist ── */}
      {items.length > 0 && (
        <div style={{ marginBottom: "0.75rem" }}>
          {visibleItems.map((item) => (
            <div
              key={item.id}
              style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.35rem", padding: "0.2rem 0" }}
            >
              {/* Custom checkbox */}
              <button
                data-testid={`toggle-item-${item.id}`}
                onClick={() => handleToggle(item)}
                style={{
                  flexShrink: 0, width: "20px", height: "20px",
                  borderRadius: "5px", border: `2px solid ${item.is_completed ? "#3d6b1f" : "rgba(26,77,46,0.3)"}`,
                  background: item.is_completed ? "#3d6b1f" : "transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 0, transition: "all 0.15s",
                }}
              >
                {item.is_completed && (
                  <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                    <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem",
                color: item.is_completed ? "#9ca3af" : "#374151",
                textDecoration: item.is_completed ? "line-through" : "none",
                flex: 1, lineHeight: 1.4,
              }}>
                {item.title}
              </span>
              <button
                onClick={() => handleDeleteItem(item.id)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#d1d5db", padding: "0.1rem", opacity: 0, transition: "opacity 0.15s" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}

          {/* Show more/less */}
          {items.length > 10 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#3d6b1f", fontSize: "0.78rem", fontFamily: "'Manrope', sans-serif", fontWeight: 600, padding: "0.25rem 0", display: "flex", alignItems: "center", gap: "0.25rem" }}
            >
              {showAll ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {showAll ? "Show less" : `Show ${hiddenCount} more`}
            </button>
          )}
        </div>
      )}

      {/* ── Add item ── */}
      {addingItem ? (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <input
            autoFocus
            data-testid={`add-item-input-${goal.id}`}
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddItem();
              if (e.key === "Escape") { setAddingItem(false); setNewItemText(""); }
            }}
            placeholder="New checklist item..."
            style={{
              flex: 1, border: "1px solid rgba(26,77,46,0.2)", borderRadius: "0.5rem",
              padding: "0.45rem 0.75rem", fontFamily: "'Manrope', sans-serif",
              fontSize: "0.82rem", color: "#1A4D2E", background: "rgba(255,255,255,0.8)", outline: "none",
            }}
          />
          <button onClick={handleAddItem} style={{ background: "#1A4D2E", color: "#fff", border: "none", borderRadius: "0.5rem", padding: "0.45rem 0.75rem", cursor: "pointer" }}>
            <Plus size={14} />
          </button>
          <button onClick={() => { setAddingItem(false); setNewItemText(""); }} style={{ background: "rgba(107,114,128,0.1)", color: "#6b7280", border: "none", borderRadius: "0.5rem", padding: "0.45rem 0.75rem", cursor: "pointer" }}>
            ✕
          </button>
        </div>
      ) : (
        <button
          data-testid={`add-item-btn-${goal.id}`}
          onClick={() => setAddingItem(true)}
          style={{
            background: "none", border: "1px dashed rgba(26,77,46,0.2)", borderRadius: "0.5rem",
            padding: "0.35rem 0.75rem", cursor: "pointer",
            fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#6b7280",
            display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.75rem",
            width: "100%",
          }}
        >
          <Plus size={12} /> Add item
        </button>
      )}

      {/* ── Motivation notes ── */}
      {goal.motivation_notes && (
        <div style={{
          background: "rgba(26,77,46,0.04)", borderRadius: "0.5rem",
          padding: "0.6rem 0.875rem", marginBottom: "0.875rem",
          borderLeft: "3px solid rgba(26,77,46,0.15)",
        }}>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.8rem", fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
            {goal.motivation_notes}
          </p>
        </div>
      )}

      {/* ── Actions ── */}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <button
          data-testid={`edit-goal-${goal.id}`}
          onClick={() => onEdit(goal)}
          style={{
            display: "flex", alignItems: "center", gap: "0.3rem",
            background: "rgba(26,77,46,0.06)", border: "1px solid rgba(26,77,46,0.15)",
            borderRadius: "2rem", padding: "0.35rem 0.875rem", cursor: "pointer",
            fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#1A4D2E", fontWeight: 600,
          }}
        >
          <Edit2 size={12} /> Edit
        </button>
        <button
          data-testid={`archive-goal-${goal.id}`}
          onClick={() => onArchive(goal.id)}
          style={{
            display: "flex", alignItems: "center", gap: "0.3rem",
            background: "rgba(107,114,128,0.06)", border: "1px solid rgba(107,114,128,0.15)",
            borderRadius: "2rem", padding: "0.35rem 0.875rem", cursor: "pointer",
            fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#6b7280", fontWeight: 600,
          }}
        >
          <Archive size={12} /> Archive
        </button>
        {confirmDelete ? (
          <div style={{ display: "flex", gap: "0.35rem", marginLeft: "auto" }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", color: "#6b7280", alignSelf: "center" }}>Delete?</span>
            <button onClick={() => onDelete(goal.id)} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "2rem", padding: "0.3rem 0.75rem", cursor: "pointer", fontSize: "0.75rem", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>Yes</button>
            <button onClick={() => setConfirmDelete(false)} style={{ background: "rgba(107,114,128,0.1)", color: "#6b7280", border: "none", borderRadius: "2rem", padding: "0.3rem 0.75rem", cursor: "pointer", fontSize: "0.75rem", fontFamily: "'Manrope', sans-serif" }}>No</button>
          </div>
        ) : (
          <button
            data-testid={`delete-goal-${goal.id}`}
            onClick={() => setConfirmDelete(true)}
            style={{
              marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
              color: "#d1d5db", padding: "0.35rem", display: "flex", alignItems: "center",
            }}
            title="Delete goal"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
